<script setup lang="ts">
import {
  getElementUiObject,
  getHtmlFromRichText,
  getCta,
  getHeadlineWithAllOptions,
  getHeadlineFontFromLegacy,
} from '@mop/cms/utils/utils';
import type { CmsUiRichText } from './UiCmsRichText';

defineOptions({
  name: 'UiCmsRichText',
});

const props = defineProps({
  data: {
    type: Object as PropType<CmsUiRichText>,
    required: true,
  },
  noPadding: {
    type: Boolean,
    default: false,
  },
});

const data: any = props.data;

const element: CmsUiRichText = getElementUiObject(data, {
  richText: getHtmlFromRichText(data.richText),
  headline: getHeadlineWithAllOptions(data),
  headlineFontStyle: data.headlineFontStyle, // Legacy, will be removed
  headlineFont: data.headlineFont,
  subline: data.subline,
  isUppercase: data.isUppercase,
  ctas: data.cta?.map(getCta),
  maxWidth: data.maxWidth,
  align: data.align,
  alignContent: data.alignContent,
});

// Legacy fallback, remove after content fully migrated
if (!element.headlineFont) {
  element.headlineFont = getHeadlineFontFromLegacy(data.headlineFontStyle, element.headline?.level || 3);
}
</script>

<template>
  <div
    v-storyblok-editable="data"
    :style="element.maxWidth !== '100%' ? `--max-width: ${element.maxWidth}` : undefined"
    :class="[
      'ui-cms-rich-text',
      {
        'ui-cms-padding': !noPadding,
        'ui-cms-rich-text--uppercase': element.isUppercase,
        'ui-cms-rich-text--center': element.align === 'center',
        'ui-cms-rich-text--right': element.align === 'right',
        'ui-cms-rich-text--content-center': element.alignContent === 'center',
      },
    ]"
    :data-track-element-item="element.headline"
  >
    <Ui2Heading
      v-if="element.headline?.text && element.headline.text !== 'undefined'"
      :font="element.headlineFont"
      :is-uppercase="element.isUppercase"
      :level="element.headline.level ?? 2"
    >
      {{ element.headline.text }}
    </Ui2Heading>
    <div v-if="element.subline" class="ui-cms-rich-text__subline">
      {{ element.subline }}
    </div>
    <div v-html="element.richText" />
    <div v-if="element.ctas && element.ctas.length > 0" class="ui-cms-rich-text__ctas">
      <div v-for="cta in element.ctas" :key="cta._uid">
        <UiCmsCta :data="cta" no-padding />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ui-cms-rich-text {
  max-width: var(--max-width, 100%);

  @include apply-upto(medium) {
    max-width: 100%;
  }
}

.ui-cms-padding {
  padding: 0 $ui-global-padding;
}

.ui-cms-rich-text--uppercase {
  text-transform: uppercase;
}

.ui-cms-rich-text--center {
  margin-left: auto;
  margin-right: auto;
}

.ui-cms-rich-text--right {
  margin-left: auto;
}

.ui-cms-rich-text--content-center {
  text-align: center;
}

.ui-cms-rich-text__subline {
  @include v2-text-style('xl');
  @include v2-apply-upto('tablet') {
    @include v2-text-style('lg');
  }
}

.ui-cms-rich-text__headline {
  margin-bottom: 0;
}

.ui-cms-rich-text__ctas {
  display: flex;
  gap: $space-16;
  margin-top: $space-16;
  align-items: center;
  flex-wrap: wrap;

  @include v2-apply-upto('tablet') {
    margin-top: $space-12;
  }
}
</style>
